import PropTypes from 'prop-types';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import SecondaryButton from '../Common/Buttons/SecondaryButton';
import { DESCRIPTION_MAX_LENGTH } from 'features/IRWorkstation/utils/reportAlertValidation';
import { useUserContext } from 'contexts/UserContext';
import {
	SEVERITY_OPTIONS,
	VISIBILITY_OPTIONS,
	TENANT_ADMIN_VISIBILITY_OPTIONS,
	COMPARISON_OPTIONS,
	CADENCE_OPTIONS,
	WEEKDAY_OPTIONS,
} from 'features/IRWorkstation/utils/alertConstants';

const CustomizeReportAlertForm = ({
	alert,
	onChange,
	titleError,
	descriptionError,
	metricThresholdError,
	dayOfWeekError,
	onTest = () => {},
	isTesting = false,
	onNameBlur,
	onSeverityChange,
	onVisibilityChange,
}) => {
	const { user } = useUserContext();
	const userIsTenantAdmin = user?.role === 'tenant_admin';
	const userVisibilityOptions = userIsTenantAdmin
		? TENANT_ADMIN_VISIBILITY_OPTIONS
		: VISIBILITY_OPTIONS;

	const handleChange = (field) => (event) => {
		onChange({
			...alert,
			[field]: event.target.value,
		});
	};

	const handleNameBlur = () => {
		onNameBlur?.();
	};

	const handleSeverityChange = (event) => {
		handleChange('severity')(event);
		onSeverityChange?.();
	};

	const handleVisibilityChange = (event) => {
		handleChange('visibility')(event);
		onVisibilityChange?.();
	};

	const handleMetricComparisonChange = (event) => {
		onChange({
			...alert,
			metric: {
				...alert.metric,
				comparison: event.target.value,
			},
		});
	};

	const handleMetricThresholdChange = (event) => {
		const { value } = event.target;
		onChange({
			...alert,
			metric: {
				...alert.metric,
				threshold: value === '' ? null : Number(value),
			},
		});
	};

	const frequency = alert.schedule?.frequency || 'nightly';
	const dayOfWeek = alert.schedule?.day_of_week || '';

	const handleCadenceChange = (event) => {
		const newFrequency = event.target.value;
		onChange({
			...alert,
			schedule: {
				frequency: newFrequency,
				...(newFrequency === 'weekly' ? { day_of_week: dayOfWeek } : {}),
			},
		});
	};

	const handleDayOfWeekChange = (event) => {
		onChange({
			...alert,
			schedule: {
				frequency: frequency === 'weekly' ? 'weekly' : frequency,
				day_of_week: event.target.value,
			},
		});
	};

	return (
		<Box
			sx={{
				backgroundColor: 'background.paper',
				border: '0.5px solid',
				borderColor: 'border.light',
				borderRadius: '8px',
				p: '12px 14px',
			}}
		>
			<Box
				sx={{
					display: 'grid',
					gridTemplateColumns: {
						xs: '1fr',
						md: 'minmax(280px, 3fr) minmax(110px, 1fr) minmax(110px, 1fr)',
					},
					gap: 1.5,
					alignItems: 'start',
				}}
			>
				<Box>
					<Typography
						variant='caption'
						color='text.secondary'
						sx={{
							display: 'block',
							fontWeight: 600,
							mb: 0.5,
							textTransform: 'uppercase',
						}}
					>
						Alert name
					</Typography>

					<TextField
						fullWidth
						size='small'
						value={alert.title || ''}
						placeholder='Enter an alert name'
						onChange={handleChange('title')}
						onBlur={handleNameBlur}
						error={Boolean(titleError)}
						helperText={titleError || ' '}
						slotProps={{
							htmlInput: {
								'aria-label': 'Alert name',
							},
						}}
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: 'background.subtle',
							},
						}}
					/>
				</Box>

				<Box>
					<Typography
						variant='caption'
						color='text.secondary'
						sx={{
							display: 'block',
							fontWeight: 600,
							mb: 0.5,
							textTransform: 'uppercase',
						}}
					>
						Severity
					</Typography>

					<TextField
						fullWidth
						select
						size='small'
						value={alert.severity}
						onChange={handleSeverityChange}
						data-testid='alert-severity-dropdown'
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: 'background.subtle',
							},
						}}
					>
						{SEVERITY_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				</Box>

				<Box>
					<Typography
						variant='caption'
						color='text.secondary'
						sx={{
							display: 'block',
							fontWeight: 600,
							mb: 0.5,
							textTransform: 'uppercase',
							'& .MuiOutlinedInput-root': {
								backgroundColor: 'background.subtle',
							},
						}}
					>
						Visibility
					</Typography>

					<TextField
						fullWidth
						select
						disabled={!userIsTenantAdmin}
						size='small'
						value={alert.visibility || 'local'}
						onChange={handleVisibilityChange}
						data-testid='alert-visibility-dropdown'
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: 'background.subtle',
							},
						}}
					>
						{userVisibilityOptions.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				</Box>
			</Box>

			<Box sx={{ mt: 1.5 }}>
				<Typography
					variant='caption'
					color='text.secondary'
					sx={{
						display: 'block',
						fontWeight: 600,
						mb: 0.5,
						textTransform: 'uppercase',
					}}
				>
					Description
				</Typography>

				<TextField
					fullWidth
					multiline
					minRows={2}
					maxRows={5}
					size='small'
					value={alert.message || ''}
					placeholder='Enter an alert description'
					onChange={handleChange('message')}
					error={Boolean(descriptionError)}
					helperText={
						descriptionError ||
						`${(alert.message || '').trim().length}/${DESCRIPTION_MAX_LENGTH}`
					}
					slotProps={{
						htmlInput: {
							'aria-label': 'Description',
						},
					}}
					sx={{
						'& .MuiOutlinedInput-root': {
							backgroundColor: 'background.subtle',
						},
					}}
					data-testid='alert-description-input'
				/>
			</Box>

			<Box sx={{ mt: 4 }}>
				<Typography
					variant='caption'
					color='text.secondary'
					sx={{
						display: 'block',
						fontWeight: 600,
						mb: 0.5,
						textTransform: 'uppercase',
					}}
				>
					Condition
				</Typography>

				<Box
					sx={{ backgroundColor: 'background.subtle', p: 2, borderRadius: 1 }}
				>
					<Box
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 2,
						}}
					>
						{/* Metric key - Display only */}
						<Typography variant='body1'>Alert when</Typography>
						<Box
							component='span'
							sx={{
								p: 2,
								borderRadius: '6px',
								backgroundColor: 'background.paper',
								border: '1px solid',
								borderColor: 'border.light',
								fontWeight: 400,
								color: 'text.secondary',
							}}
							data-testid='alert-metric-key-input'
						>
							{alert.metric?.metric_key}
						</Box>
						<Typography variant='body1'>is</Typography>
					</Box>

					<Box
						sx={{
							mt: 1.5,
							gap: 1.5,
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
						}}
					>
						<TextField
							select
							size='small'
							value={alert.metric?.comparison || ''}
							onChange={handleMetricComparisonChange}
							data-testid='alert-comparison-dropdown'
							helperText={' '} // Empty helper text to maintain consistent height with threshold input
							sx={{
								flex: 1,
								'& .MuiOutlinedInput-root': {
									backgroundColor: 'background.paper',
								},
							}}
						>
							{COMPARISON_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</TextField>

						<TextField
							type='number'
							size='small'
							value={alert.metric?.threshold ?? ''}
							placeholder='Enter a threshold value'
							onChange={handleMetricThresholdChange}
							error={Boolean(metricThresholdError)}
							helperText={metricThresholdError || ' '}
							slotProps={{
								htmlInput: {
									'aria-label': 'Threshold',
									step: 'any', // Allow decimal values
								},
							}}
							data-testid='alert-threshold-input'
							sx={{
								flex: 1,
								'& .MuiOutlinedInput-root': {
									backgroundColor: 'background.paper',
								},
							}}
						/>
					</Box>
				</Box>
			</Box>

			<Box sx={{ mt: 1 }}>
				<Typography
					variant='caption'
					color='text.secondary'
					sx={{
						display: 'block',
						fontWeight: 600,
						mb: 0.5,
						textTransform: 'uppercase',
					}}
				>
					Cadence
				</Typography>

				<Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
					<TextField
						fullWidth
						select
						size='small'
						value={frequency}
						onChange={handleCadenceChange}
						data-testid='alert-cadence-dropdown'
						slotProps={{
							htmlInput: {
								'aria-label': 'Cadence',
							},
						}}
						sx={{
							flex: 1,
							'& .MuiOutlinedInput-root': {
								backgroundColor: 'background.subtle',
							},
						}}
					>
						{CADENCE_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>

					<TextField
						fullWidth
						select
						size='small'
						disabled={frequency !== 'weekly'}
						value={frequency === 'weekly' ? dayOfWeek : ''}
						onChange={handleDayOfWeekChange}
						error={Boolean(dayOfWeekError && frequency === 'weekly')}
						helperText={frequency === 'weekly' ? dayOfWeekError : undefined}
						data-testid='alert-day-of-week-dropdown'
						slotProps={{
							htmlInput: {
								'aria-label': 'Day of week',
							},
							select: {
								displayEmpty: true,
							},
						}}
						sx={{
							flex: 1,
							'& .MuiOutlinedInput-root': {
								backgroundColor: 'background.subtle',
							},
						}}
					>
						<MenuItem value='' disabled>
							Day of Week
						</MenuItem>
						{WEEKDAY_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				</Box>
			</Box>

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'flex-end',
					mt: 2,
				}}
			>
				<SecondaryButton
					onClick={onTest}
					label={isTesting ? 'Testing Alert...' : 'Test Alert'}
					loading={isTesting}
					data-testid='test-alert-button'
				/>
			</Box>
		</Box>
	);
};

CustomizeReportAlertForm.propTypes = {
	alert: PropTypes.shape({
		title: PropTypes.string.isRequired,
		message: PropTypes.string,
		severity: PropTypes.string.isRequired,
		visibility: PropTypes.string.isRequired,
		schedule: PropTypes.shape({
			frequency: PropTypes.string,
			day_of_week: PropTypes.string,
		}),
	}).isRequired,
	onChange: PropTypes.func.isRequired,
	onTest: PropTypes.func,
	isTesting: PropTypes.bool,
	titleError: PropTypes.string,
	descriptionError: PropTypes.string,
	metricThresholdError: PropTypes.string,
	dayOfWeekError: PropTypes.string,
	onNameBlur: PropTypes.func,
	onSeverityChange: PropTypes.func,
	onVisibilityChange: PropTypes.func,
};

export default CustomizeReportAlertForm;
